/**
 * Pure page/section helpers shared by the Compare PDF viewer and the export
 * gather (no React, so the export worker can import them).
 */
import type { TOCEntry } from '../types/viewer';

/** The TOC fields the page maths reads (the export's trimmed TOC fits too). */
type TocLike = {
  section_number?: TOCEntry['section_number'] | undefined;
  page_start?: TOCEntry['page_start'] | undefined;
  page_end?: TOCEntry['page_end'] | undefined;
};

/** Highest page number the document actually has, or null when unknown.
 *  Uses max(page_num) rather than `pages.length`: the list is not guaranteed
 *  to be dense, and a length-based bound would reject legitimate pages. */
export function maxPageNumOf(
  pages: { page_num: number }[] | null | undefined,
): number | null {
  if (!pages || pages.length === 0) return null;
  return pages.reduce((m, p) => (p.page_num > m ? p.page_num : m), 0) || null;
}

/** The family rule shared by the page filter and the export's section text: a
 *  section matches a requested number when equal or a subsection (``n + '.'``). */
export function sectionFamilyMatcher(sectionNumbers: string[]): (sn: string | null | undefined) => boolean {
  const wanted = sectionNumbers.map((s) => s.trim().replace(/\.$/, '')).filter(Boolean);
  return (sn) => !!sn && wanted.some((w) => sn === w || sn.startsWith(w + '.'));
}

/**
 * Page numbers covered by the given section numbers — the Compare view's
 * "show only this section's pages" filter. For each requested number, matches
 * every TOC entry whose section_number equals it OR starts with ``n + '.'`` (the
 * same family boundary as ``findSectionTocEntry``/``expandSectionFamilies``, so
 * "4.4" pulls 4.4.1/4.4.2). Each matched entry contributes ``page_start`` ..
 * ``page_end``; a null ``page_end`` is derived from the next entry's
 * ``page_start`` (−1), or the document's last page for the final section.
 * Returns an EMPTY set when nothing matches — the caller then shows all pages
 * rather than blanking the column. Pure/testable.
 */
export function pageNumsForSections(
  tocEntries: readonly TocLike[],
  pages: { page_num: number }[] | null | undefined,
  sectionNumbers: string[],
): Set<number> {
  const result = new Set<number>();
  if (sectionNumbers.length === 0 || tocEntries.length === 0) return result;
  const matches = sectionFamilyMatcher(sectionNumbers);
  // Entry start pages, ascending — to derive a missing page_end from the next
  // section's start.
  const starts = tocEntries
    .map((e) => e.page_start)
    .filter((p): p is number => p != null)
    .sort((a, b) => a - b);
  const docEnd = maxPageNumOf(pages);
  for (const e of tocEntries) {
    const start = e.page_start;
    if (start == null || !matches(e.section_number)) continue;
    let end = e.page_end;
    if (end == null) {
      const nextStart = starts.find((p) => p > start);
      end = nextStart != null ? nextStart - 1 : docEnd;
    }
    if (end == null || end < start) end = start; // never an empty/negative span
    for (let p = start; p <= end; p++) result.add(p);
  }
  return result;
}
